import { tWithKeyAttr as t } from "../../i18nHelper";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./filterButton.module.css";
import filterIcon from "../../images/filter_icon.svg";
import {
    filterOptionsTreatmentPlan as options,
} from "../../utils/constants";
import { isToday } from "../../utils/utils";
import { ITreatmentPlan } from "../../types/types";
import i18n from "../../i18n";

function FilterButtonTreatmentPlan<
    T extends ITreatmentPlan
>({
    documentsToFilter,
    setDocumentsToFilter,
}: {
    documentsToFilter: T[] | undefined;
    setDocumentsToFilter: (users: ITreatmentPlan[] | undefined) => void;
}) {
    const [filtersVisible, setFiltersVisible] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left?: number; right?: number } | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    
    const [filterValue, setFilterValue] = useState<undefined | string>(undefined);
    const today = new Date();

  // Professional click-outside and scroll handler
  useEffect(() => {
    if (!filtersVisible) return;

    // If dropdown overflows viewport bottom, reposition upward
    const raf = requestAnimationFrame(() => {
      if (!buttonRef.current) return;
      const dropdown = containerRef.current?.querySelector(`.${styles.filterOptionsFixed}`) as HTMLElement | null;
      if (dropdown) {
        const dropdownHeight = dropdown.getBoundingClientRect().height;
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - buttonRect.bottom;
        if (dropdownHeight > spaceBelow) {
          // Not enough space below — open upward
          setDropdownPosition(prev => prev ? { ...prev, top: buttonRect.top - dropdownHeight - 5 } : prev);
        }
      }
    });

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setFiltersVisible(false);
      }
    };

    const handleScroll = () => {
      setFiltersVisible(false);
    };

    // Use timeout to avoid closing immediately on open
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }, 0);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [filtersVisible]);

    useEffect(() => {
        if (filterValue === "clearFilters") {
            setFilterValue(undefined);
        } else if (filterValue === "today") {
            if (documentsToFilter) {
                setDocumentsToFilter(
                    documentsToFilter.filter((d) => isToday(d.appointmentsProgram.createDate))
                );
            }

        } else if (filterValue === "week") {
            const startOfCurrentWeek = new Date(today);
            startOfCurrentWeek.setDate(today.getDate() - today.getDay());

            // Calculate the start of the last week (Sunday)
            const startOfLastWeek = new Date(startOfCurrentWeek);
            startOfLastWeek.setDate(startOfCurrentWeek.getDate() - 7);
            documentsToFilter &&
                setDocumentsToFilter(
                    documentsToFilter.filter(
                        (d) =>
                            new Date(d.appointmentsProgram.createDate) >= startOfLastWeek &&
                            new Date(d.appointmentsProgram.createDate) <= today
                    )
                );
        } else if (filterValue === "month") {
            const startOfCurrentMonth = new Date(
                today.getFullYear(),
                today.getMonth(),
                1
            );

            setDocumentsToFilter(
                documentsToFilter?.filter(
                    (d) => new Date(d.appointmentsProgram.createDate) >= startOfCurrentMonth
                )
            );
        }

        else {
            setDocumentsToFilter(documentsToFilter);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterValue, documentsToFilter]);

const lang = i18n.language;

  const dir = useMemo(() => {
    return lang === "he" || lang === "ar" ? "rtl" : "ltr";
  }, [lang]);

  const handleToggleDropdown = useCallback(() => {
    if (!filtersVisible && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const isRTL = dir === 'rtl';
      
      setDropdownPosition(
        isRTL
          ? { top: rect.bottom + 5, right: window.innerWidth - rect.right }
          : { top: rect.bottom + 5, left: rect.left }
      );
    }
    setFiltersVisible(prev => !prev);
  }, [dir, filtersVisible]);

    return (
        <div className={`${styles.container} ${styles.center}`} dir={dir} lang={lang} ref={containerRef}>
            <button
                ref={buttonRef}
                className={`${styles.btn} ${styles.btn_type_filter} hover`}
                onClick={handleToggleDropdown}
            >
                <img className={styles.filterIcon} src={filterIcon} alt={t("FILTER_TREATMENT_PLAN_BUTTON_ALT")} />
                {t("FILTER_TREATMENT_PLAN_BUTTON_TEXT")}
            </button>
            {filtersVisible && dropdownPosition && (
                <ul 
                    className={styles.filterOptionsFixed}
                    style={{
                        top: `${dropdownPosition.top}px`,
                        left: dropdownPosition.left !== undefined ? `${dropdownPosition.left}px` : 'auto',
                        right: dropdownPosition.right !== undefined ? `${dropdownPosition.right}px` : undefined
                    }}
                >
                    {options.map((f, index) => (
                        <li
                            key={index}
                            className={`${styles.filterOption}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                setFilterValue(f.value);
                                if (f.value !== "status") {
                                    setFiltersVisible(false);
                                }
                            }}
                        >
                            <input
                                type="radio"
                                value={f.value}
                                id={f.value}
                                name="sort"
                                className={styles.radio}
                                checked={filterValue === f.value}
                                onChange={() => { }}
                            />
                            <label htmlFor={f.value} className={styles.label}>
                                {t(f.label)}
                            </label>

                        </li>
                    ))}
                    <li
                        className={styles.filterOption}
                        onClick={() => {
                            setFilterValue("clearFilters");
                            setFiltersVisible(false);
                        }}
                    >
                        <input
                            type="radio"
                            value="clearFilters"
                            id="clearFilters"
                            name="sort"
                            className={styles.radio}
                            checked={filterValue === "clearFilters"}
                            onChange={() => { }}
                        />
                        <label htmlFor="clearFilters" className={styles.label}>
                            {t("FILTER_TREATMENT_PLAN_CLEAR_LABEL")}                        </label>
                    </li>
                </ul>
            )}
            {!!filterValue && <span>!</span>}
        </div>
    );
}

export default FilterButtonTreatmentPlan;
